// src/pages/Tools.js
// Interactive Tools Console - Security Testing Utilities
// DriftAlarm.AI - Built by Security Experts

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// Note: useMsal available if needed for account info
import tierService from '../services/tierService';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import {
  TOOLS,
  executeAndWait,
  encodeUtility,
  formatCommandSummary,
  generateHistoryId
} from '../services/toolsService';
import './Tools.css';

function Tools() {
  const navigate = useNavigate();
  const resultsContainerRef = useRef(null);

  // Core state
  const [userTier, setUserTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tool state
  const [selectedTool, setSelectedTool] = useState('curl');
  const [formData, setFormData] = useState({});
  const [executing, setExecuting] = useState(false);

  // Results history
  const [resultsHistory, setResultsHistory] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});

  // All tiers have access to tools
  const hasAccess = true;

  // Get current tool definition
  const currentTool = TOOLS.find(t => t.id === selectedTool) || TOOLS[0];

  // Load user tier
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const tierInfo = await tierService.getCachedTierInfo();
        setUserTier(tierInfo?.tier || 'free');
      } catch (err) {
        console.error('Failed to load tier info:', err);
      } finally {
        setLoading(false);
      }
    };
    loadUserProfile();
  }, []);

  // Reset form when tool changes
  useEffect(() => {
    const tool = TOOLS.find(t => t.id === selectedTool) || TOOLS[0];
    const defaultValues = {};
    tool.fields.forEach(field => {
      if (field.default !== undefined) {
        defaultValues[field.name] = field.default;
      }
    });
    setFormData(defaultValues);
  }, [selectedTool]);

  // Handle form input changes
  const handleInputChange = useCallback((fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  }, []);

  // Toggle expanded state for a result item
  const toggleExpand = useCallback((itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  }, []);

  // Execute tool
  const handleExecute = async () => {
    // Validate required fields
    const missingFields = currentTool.fields
      .filter(f => f.required && !formData[f.name])
      .map(f => f.label);

    if (missingFields.length > 0) {
      setError(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    setError(null);
    setExecuting(true);

    const historyItem = {
      id: generateHistoryId(),
      tool: selectedTool,
      toolName: currentTool.name,
      icon: currentTool.icon,
      command: formatCommandSummary(selectedTool, formData),
      params: { ...formData },
      timestamp: Date.now(),
      status: 'executing',
      result: null
    };

    // Add to history immediately (executing state)
    setResultsHistory(prev => [historyItem, ...prev]);

    try {
      let result;

      if (currentTool.serverSide) {
        // Server-side execution
        result = await executeAndWait(selectedTool, formData);
      } else {
        // Client-side execution (encoder)
        result = encodeUtility(formData.input, formData.operation);
      }

      // Update history item with result
      setResultsHistory(prev =>
        prev.map(item =>
          item.id === historyItem.id
            ? { ...item, status: result.success !== false ? 'success' : 'error', result }
            : item
        )
      );

      // Auto-expand the new result
      setExpandedItems(prev => ({ ...prev, [historyItem.id]: true }));

    } catch (err) {
      console.error('Tool execution error:', err);

      // Update history item with error
      setResultsHistory(prev =>
        prev.map(item =>
          item.id === historyItem.id
            ? { ...item, status: 'error', result: { error: err.message } }
            : item
        )
      );

      if (err.upgradeRequired) {
        setError('This feature requires a higher tier subscription.');
      }
    } finally {
      setExecuting(false);
    }
  };

  // Clear history
  const handleClearHistory = () => {
    setResultsHistory([]);
    setExpandedItems({});
  };

  // Render form field based on type
  const renderField = (field) => {
    // Check conditional visibility
    if (field.conditional) {
      const { field: condField, value: condValue } = field.conditional;
      if (formData[condField] !== condValue) {
        return null;
      }
    }

    const value = formData[field.name] || '';

    switch (field.type) {
      case 'url':
      case 'text':
        return (
          <div className={`form-field ${field.hint ? 'form-field-with-hint' : ''}`} key={field.name}>
            <label htmlFor={field.name}>{field.label}</label>
            {field.hint && <span className="field-hint">{field.hint}</span>}
            <input
              type={field.type === 'url' ? 'url' : 'text'}
              id={field.name}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
            />
          </div>
        );

      case 'number':
        return (
          <div className="form-field" key={field.name}>
            <label htmlFor={field.name}>{field.label}</label>
            <input
              type="number"
              id={field.name}
              value={value}
              onChange={(e) => handleInputChange(field.name, parseInt(e.target.value) || '')}
              min={field.min}
              max={field.max}
              placeholder={field.default?.toString()}
            />
          </div>
        );

      case 'select':
        return (
          <div className="form-field" key={field.name}>
            <label htmlFor={field.name}>{field.label}</label>
            <select
              id={field.name}
              value={value || field.default}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            >
              {field.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );

      case 'textarea':
        return (
          <div className="form-field form-field-full" key={field.name}>
            <label htmlFor={field.name}>{field.label}</label>
            <textarea
              id={field.name}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              rows={4}
            />
          </div>
        );

      case 'checkbox':
        return (
          <div className="form-field form-field-checkbox" key={field.name}>
            <label htmlFor={field.name} className="checkbox-label">
              <input
                type="checkbox"
                id={field.name}
                checked={formData[field.name] !== undefined ? formData[field.name] : field.default}
                onChange={(e) => handleInputChange(field.name, e.target.checked)}
              />
              <span className="checkbox-text">{field.label}</span>
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  // Render result content based on tool type
  const renderResultContent = (item) => {
    const { result, tool } = item;

    if (!result) return null;

    if (result.error) {
      return (
        <div className="result-error">
          <span className="error-icon">!</span>
          <span>{result.error}</span>
        </div>
      );
    }

    // Encoder result (client-side)
    if (tool === 'encoder') {
      return (
        <div className="result-content">
          <div className="result-section">
            <h4>Output</h4>
            <pre className="result-code">{result.result}</pre>
          </div>
        </div>
      );
    }

    // Server-side results
    const data = result.result || result;

    switch (tool) {
      case 'curl':
        return (
          <div className="result-content">
            <div className="result-summary">
              <span className={`status-badge status-${data.statusCode >= 200 && data.statusCode < 400 ? 'success' : 'warning'}`}>
                {data.statusCode}
              </span>
              <span className="result-meta">
                {data.responseSize ? `${(data.responseSize / 1024).toFixed(2)} KB` : ''}
                {data.truncated && <span className="truncated-badge">Truncated</span>}
              </span>
            </div>
            {data.headers && Object.keys(data.headers).length > 0 && (
              <div className="result-section collapsible">
                <h4>Response Headers</h4>
                <pre className="result-code">
                  {Object.entries(data.headers).map(([k, v]) => `${k}: ${v}`).join('\n')}
                </pre>
              </div>
            )}
            {data.body && (
              <div className="result-section collapsible">
                <h4>Response Body</h4>
                <pre className="result-code">{data.body}</pre>
              </div>
            )}
          </div>
        );

      case 'headers':
        return (
          <div className="result-content">
            <div className="result-summary">
              <span className={`score-badge score-${data.score >= 80 ? 'good' : data.score >= 50 ? 'medium' : 'poor'}`}>
                Score: {data.score}%
              </span>
            </div>
            {data.present?.length > 0 && (
              <div className="result-section">
                <h4>Present Headers</h4>
                <ul className="header-list present">
                  {data.present.map(h => <li key={h}>{h}</li>)}
                </ul>
              </div>
            )}
            {data.missing?.length > 0 && (
              <div className="result-section">
                <h4>Missing Headers</h4>
                <ul className="header-list missing">
                  {data.missing.map(m => (
                    <li key={m.header}>
                      <span className={`severity-${m.severity}`}>{m.header}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'ssl':
        return (
          <div className="result-content">
            <div className="result-summary">
              <span className={`grade-badge grade-${data.grade?.toLowerCase()}`}>
                Grade: {data.grade || 'Unknown'}
              </span>
              {data.certificate?.daysUntilExpiry !== undefined && (
                <span className={`expiry-badge ${data.certificate.daysUntilExpiry < 30 ? 'warning' : ''}`}>
                  Expires in {data.certificate.daysUntilExpiry} days
                </span>
              )}
            </div>
            {data.certificate && (
              <div className="result-section">
                <h4>Certificate Info</h4>
                <pre className="result-code">
                  {`Subject: ${JSON.stringify(data.certificate.subject, null, 2)}
Issuer: ${JSON.stringify(data.certificate.issuer, null, 2)}
Valid From: ${data.certificate.notBefore}
Valid Until: ${data.certificate.notAfter}`}
                </pre>
              </div>
            )}
            {data.issues?.length > 0 && (
              <div className="result-section">
                <h4>Issues Found</h4>
                <ul className="issues-list">
                  {data.issues.map((issue, i) => <li key={i}>{issue}</li>)}
                </ul>
              </div>
            )}
          </div>
        );

      case 'dns':
        return (
          <div className="result-content">
            {data.records && Object.entries(data.records).map(([type, records]) => (
              <div className="result-section" key={type}>
                <h4>{type} Records</h4>
                <pre className="result-code">
                  {Array.isArray(records) ? records.join('\n') : JSON.stringify(records, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        );

      case 'whois':
        return (
          <div className="result-content">
            <div className="result-section">
              <pre className="result-code whois-result">
                {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
              </pre>
            </div>
          </div>
        );

      case 'port':
        return (
          <div className="result-content">
            {data.openPorts?.length > 0 && (
              <div className="result-section">
                <h4>Open Ports</h4>
                <div className="ports-grid">
                  {data.openPorts.map(p => (
                    <span key={p.port} className="port-badge open">
                      {p.port}{p.service ? ` (${p.service})` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {data.closedPorts?.length > 0 && (
              <div className="result-section">
                <h4>Closed/Filtered ({data.closedPorts.length})</h4>
                <span className="ports-summary">{data.closedPorts.slice(0, 20).join(', ')}...</span>
              </div>
            )}
          </div>
        );

      case 'subdomain':
        return (
          <div className="result-content">
            <div className="result-summary">
              <span className="count-badge">{data.subdomains?.length || 0} subdomains found</span>
            </div>
            {data.subdomains?.length > 0 && (
              <div className="result-section">
                <pre className="result-code">
                  {data.subdomains.join('\n')}
                </pre>
              </div>
            )}
          </div>
        );

      case 'cloud':
        return (
          <div className="result-content">
            <div className="result-summary">
              <span className="count-badge">{data.summary?.total || 0} storage resources found</span>
              {data.summary?.publicReadCount > 0 && (
                <span className="access-badge access-public-read">{data.summary.publicReadCount} public read</span>
              )}
              {data.summary?.publicWriteCount > 0 && (
                <span className="access-badge access-public-write">{data.summary.publicWriteCount} public write</span>
              )}
            </div>

            {/* AWS S3 Buckets */}
            {data.aws?.buckets?.length > 0 && (
              <div className="result-section">
                <h4>AWS S3 Buckets ({data.aws.buckets.length})</h4>
                <ul className="bucket-list">
                  {data.aws.buckets.map((bucket, i) => (
                    <li key={i} className={`bucket-item access-${bucket.access}`}>
                      <span className="bucket-name">{bucket.name}</span>
                      <span className={`access-badge access-${bucket.access}`}>{bucket.access}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Azure Tenant Discovery */}
            {(data.azure?.tenantId || data.azure?.tenants?.length > 0) && (
              <div className="result-section">
                <h4>Azure Tenant Discovery</h4>
                {data.azure.tenants?.map((tenant, i) => (
                  <div key={i} className="tenant-info">
                    <span className="tenant-label">Domain:</span>
                    <code className="tenant-domain">{tenant.domain}</code>
                    <span className="tenant-label">Tenant ID:</span>
                    <code className="tenant-id">{tenant.tenantId}</code>
                  </div>
                ))}
                {!data.azure.tenants?.length && data.azure.tenantId && (
                  <div className="tenant-info">
                    <span className="tenant-label">Tenant ID:</span>
                    <code className="tenant-id">{data.azure.tenantId}</code>
                  </div>
                )}
              </div>
            )}

            {/* Azure Blob Containers */}
            {data.azure?.containers?.length > 0 && (
              <div className="result-section">
                <h4>Azure Blob Storage ({data.azure.containers.length})</h4>
                <ul className="bucket-list">
                  {data.azure.containers.map((container, i) => (
                    <li key={i} className={`bucket-item access-${container.access}`}>
                      <span className="bucket-name">
                        {container.account}{container.container && `/${container.container}`}
                      </span>
                      <span className={`access-badge access-${container.access}`}>{container.access}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* GCP Storage Buckets */}
            {data.gcp?.buckets?.length > 0 && (
              <div className="result-section">
                <h4>GCP Cloud Storage ({data.gcp.buckets.length})</h4>
                <ul className="bucket-list">
                  {data.gcp.buckets.map((bucket, i) => (
                    <li key={i} className={`bucket-item access-${bucket.access}`}>
                      <span className="bucket-name">{bucket.name}</span>
                      <span className={`access-badge access-${bucket.access}`}>{bucket.access}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* No results message */}
            {(!data.aws?.buckets?.length && !data.azure?.containers?.length && !data.gcp?.buckets?.length) && (
              <div className="result-section">
                <p className="no-results">No public cloud storage found for the provided keywords.</p>
              </div>
            )}

            {/* Critical findings */}
            {data.findings?.length > 0 && (
              <div className="result-section">
                <h4>Security Findings</h4>
                <ul className="findings-list">
                  {data.findings.map((finding, i) => (
                    <li key={i} className={`finding-item severity-${finding.severity}`}>
                      <span className={`severity-badge severity-${finding.severity}`}>{finding.severity}</span>
                      <span className="finding-type">{finding.type}</span>
                      {finding.bucket && <span className="finding-target">{finding.bucket}</span>}
                      {finding.account && <span className="finding-target">{finding.account}</span>}
                      {finding.domain && <span className="finding-target">{finding.domain}</span>}
                      {finding.tenantId && <span className="finding-target tenant">{finding.tenantId}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="result-content">
            <pre className="result-code">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        );
    }
  };

  // Loading state
  if (loading) {
    return (
      <CleanModernLayout>
        <div className="tools-page">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </CleanModernLayout>
    );
  }

  // Upgrade required state
  if (!hasAccess) {
    return (
      <CleanModernLayout>
        <div className="tools-page">
          <div className="upgrade-required-container">
          <div className="upgrade-icon">🛠️</div>
          <h1>Interactive Tools</h1>
          <p className="upgrade-description">
            Access powerful security testing tools for manual vulnerability validation.
            Execute curl commands, analyze headers, check SSL, query DNS, and more.
          </p>

          <div className="feature-preview">
            <h2>Available Tools</h2>
            <div className="tools-preview-grid">
              {TOOLS.map(tool => (
                <div key={tool.id} className="tool-preview-item">
                  <span className="tool-icon">{tool.icon}</span>
                  <span className="tool-name">{tool.name}</span>
                </div>
              ))}
            </div>
          </div>

          <button className="upgrade-cta" onClick={() => navigate('/upgrade')}>
            Upgrade to Standard
          </button>
          <p className="tier-note">
            Current tier: <span className="tier-badge tier-{userTier}">{userTier}</span>
          </p>
        </div>
        </div>
      </CleanModernLayout>
    );
  }

  // Main tools console
  return (
    <CleanModernLayout>
    <div className="tools-page">
      <div className="tools-console">
        {/* Header */}
        <div className="console-header">
          <div className="header-left">
            <h1>
              <span className="header-icon">🛠️</span>
              Tools Console
            </h1>
          </div>
          <div className="header-right">
            <span className={`tier-badge tier-${userTier}`}>{userTier}</span>
            {resultsHistory.length > 0 && (
              <button className="clear-history-btn" onClick={handleClearHistory}>
                Clear History
              </button>
            )}
          </div>
        </div>

        {/* Tool Selector Tabs */}
        <div className="tool-tabs">
          {TOOLS.map(tool => (
            <button
              key={tool.id}
              className={`tool-tab ${selectedTool === tool.id ? 'active' : ''}`}
              onClick={() => setSelectedTool(tool.id)}
            >
              <span className="tab-icon">{tool.icon}</span>
              <span className="tab-name">{tool.name}</span>
            </button>
          ))}
        </div>

        {/* Input Section */}
        <div className="input-section">
          <div className="tool-description">
            <span className="tool-icon-large">{currentTool.icon}</span>
            <span className="tool-desc-text">{currentTool.description}</span>
            {!currentTool.serverSide && (
              <span className="client-side-badge">Client-side</span>
            )}
          </div>

          {error && (
            <div className="error-banner">
              <span>{error}</span>
              <button onClick={() => setError(null)}>✕</button>
            </div>
          )}

          <form className="tool-form" onSubmit={(e) => { e.preventDefault(); handleExecute(); }}>
            <div className="form-fields">
              {(() => {
                const fields = currentTool.fields;
                const result = [];
                let checkboxGroup = [];

                fields.forEach((field, index) => {
                  if (field.type === 'checkbox') {
                    checkboxGroup.push(field);
                    // If next field is not a checkbox or this is the last field, render the group
                    const nextField = fields[index + 1];
                    if (!nextField || nextField.type !== 'checkbox') {
                      result.push(
                        <div className="checkbox-wrapper" key={`checkbox-group-${index}`}>
                          {checkboxGroup.map(f => renderField(f))}
                        </div>
                      );
                      checkboxGroup = [];
                    }
                  } else {
                    result.push(renderField(field));
                  }
                });

                return result;
              })()}
            </div>

            <button
              type="submit"
              className="execute-btn"
              disabled={executing}
            >
              {executing ? (
                <>
                  <span className="btn-spinner"></span>
                  Executing...
                </>
              ) : (
                <>
                  <span className="btn-icon">▶</span>
                  Execute
                </>
              )}
            </button>
          </form>
        </div>

        {/* Results History */}
        <div className="results-section" ref={resultsContainerRef}>
          {resultsHistory.length === 0 ? (
            <div className="empty-results">
              <p>No results yet. Select a tool and execute a command.</p>
            </div>
          ) : (
            <div className="results-list">
              {resultsHistory.map(item => (
                <div
                  key={item.id}
                  className={`result-item status-${item.status} ${expandedItems[item.id] ? 'expanded' : ''}`}
                >
                  <div
                    className="result-header"
                    onClick={() => toggleExpand(item.id)}
                  >
                    <div className="result-command">
                      <span className="prompt">&gt;</span>
                      <span className="tool-icon">{item.icon}</span>
                      <span className="command-text">{item.command}</span>
                    </div>
                    <div className="result-meta">
                      <span className={`status-indicator status-${item.status}`}>
                        {item.status === 'executing' ? '...' : item.status === 'success' ? '✓' : '✕'}
                      </span>
                      <span className="timestamp">
                        {new Date(item.timestamp).toLocaleTimeString()}
                      </span>
                      <span className={`expand-arrow ${expandedItems[item.id] ? 'expanded' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </div>

                  {expandedItems[item.id] && item.status !== 'executing' && (
                    <div className="result-body">
                      {renderResultContent(item)}
                    </div>
                  )}

                  {item.status === 'executing' && (
                    <div className="result-body">
                      <div className="executing-indicator">
                        <span className="exec-spinner"></span>
                        <span>Executing {item.toolName}...</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </CleanModernLayout>
  );
}

export default Tools;
